import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { etherealBiome } from '../biomeArchetypes';
import { ATMOSPHERE_PALETTE as P } from '../palette';

/** Celestial / Aether Expanse — an ethereal OPEN star-sky realm: a deep starry indigo→violet
 *  sky, drifting nebula cloud bands, a luminous pale moon, twinkling star motes, soft aurora-like
 *  cool light shafts, a dreamlike violet light bed, rising stardust, and distant high star-cluster
 *  twinkle. Dreamlike + serene + otherworldly + VAST. Distinct from the Nexus (a focused violet
 *  vortex over a dais) — this is OPEN sky: minimal floating ground, everything leans on the
 *  sky + glow. Kept deep + saturated (a 'normal' grade, not a pale 'screen' lift). */
export const CELESTIAL = etherealBiome({
  id: 'celestial',
  sky: { from: 0x0a0a2e, to: 0x2a1a52 },
  celestial: { atX: 0.66, atY: 0.17, color: 0xeef0ff, haloColor: 0xb9b0ff, size: 122, haloScale: 2.8, coreAlpha: 0.82, haloAlpha: 0.2 },
  cloudBands: { count: 5, color: 0x6a5cb0, sizeMin: 320, sizeMax: 600, alphaMin: 0.05, alphaMax: 0.13, driftPxPerSec: 4, bandYMin: 0.05, bandYMax: 0.30 },
  extraSkyLayers: [
    // Aurora — dancing VERTICAL cyan/teal/violet light curtains hanging from the night
    // sky (tall ray_ribbon shafts, the way godRays render; they sway + lean + shimmer).
    {
      kind: 'aurora', colors: [0x33ffa0, 0x00ffc8, 0x66ffd8, 0xa86cff],
      bands: 4, widthMin: 30, widthMax: 60, heightMin: 160, heightMax: 280,
      alphaMin: 0.06, alphaMax: 0.18, bandYMin: 0.0, bandYMax: 0.04,
      swayPx: 24, periodMinMs: 6000, periodMaxMs: 11000, driftPxPerSec: 5,
    },
    // Occasional meteor streaks crossing the vast sky — sparse, pale white-blue,
    // flying down-right and fading (the wishing-star beat of an open star realm).
    {
      kind: 'shootingStar', color: 0xeaf2ff, count: 2,
      intervalMinMs: 6000, intervalMaxMs: 14000, lengthMin: 90, lengthMax: 170,
      thickness: 5, speed: 620, angleDeg: 24, alphaMax: 0.85,
    },
  ],
  motes: { textureKey: 'sparkle', colors: [0xeef2ff, P.frostCore, P.crystal, P.softPink],
    count: 30, sizeMin: 3, sizeMax: 9, driftPx: 16,
    periodMinMs: 4000, periodMaxMs: 9000, alphaMin: 0.2, alphaMax: 0.85 },
  rays: { color: 0x5fd0e8, widthMin: 80, widthMax: 180, height: 540,
    tiltDeg: 6, periodMinMs: 9000, periodMaxMs: 16000, alphaMin: 0.07, alphaMax: 0.28 },
  glow: {
    blobs: [
      { color: 0xb9b0ff, atX: 0.66, atY: 0.17, size: 460, alpha: 0.08 },
      { color: 0x5a4ca0, atX: 0.3, atY: 0.48, size: 540, alpha: 0.06 },
      { color: P.frostCore, atX: 0.5, atY: 0.94, size: 560, alpha: 0.06 },
    ],
    breathe: { periodMs: 6400, min: 0.75, max: 1.3 },
  },
  lightField: { palette: [0x7a6cc0, P.frostCore, P.mint, P.softPink],
    count: 16, sizeMin: 100, sizeMax: 260, alphaMin: 0.04, alphaMax: 0.15,
    driftRadius: 64, periodMinMs: 10000, periodMaxMs: 18000 },
  weather: { mode: 'rise', color: 0xcdd4ff, colorEnd: 0x6a5cb0,
    size: 10, frequencyMs: 160, lifespanMs: 5200, speed: 16, gravityY: -10, windX: 8, alphaEnd: 0.5,
    gust: { periodMs: 8000, min: 0.5, max: 1.45, phase: 0.25 } },
  flicker: { color: 0xcfd6ff, size: 220, baseAlpha: 0.05, flickerAmp: 0.05, hzMin: 1, hzMax: 4,
    anchors: [{ atX: 0.18, atY: 0.14 }, { atX: 0.52, atY: 0.09 }, { atX: 0.84, atY: 0.2 }] },
  parallax: [
    { svg: OTHERWORLD_BACKDROPS.celestial.far, shape: 'peaks', color: 0x4a4080, speed: 0.03, height: 320, alpha: 0.4, detail: 0.7, sway: { ampPx: 14, periodMs: 38000 } },
    { svg: OTHERWORLD_BACKDROPS.celestial.mid, shape: 'peaks', color: 0x39306a, speed: 0.07, height: 230, alpha: 0.52, detail: 0.9, sway: { ampPx: 10, periodMs: 30000 } },
  ],
  fog: { color: 0x5a4ca0, bands: 5, alphaMin: 0.03, alphaMax: 0.1, driftPx: 60, periodMinMs: 12000, periodMaxMs: 22000 },
  closer: {
    vignette: { color: 0x080616, edgeAlpha: 0.34, washColor: 0x4a3a8a, washAlpha: 0.05 },
    grade: { top: 0x6a5cff, bottom: 0x14102e, strength: 0.12, blend: 'normal' },
  },
});
